/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { getAllRaiseInvoices } from "services/financeService";

// @mui material components
import Card from "@mui/material/Card";
import TableSortLabel from "@mui/material/TableSortLabel";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { Link } from "react-router-dom";
// Data
// import ClientBalanceTableData from "Views/FinanceManagment/ClientBalance/data/ClientBalanceTableData"

const profileLinkSx = (enabled) => ({
  cursor: enabled ? "pointer" : "default",
  textDecoration: enabled ? "underline !important" : "none",
  textUnderlineOffset: enabled ? "2px" : undefined,
  textDecorationThickness: enabled ? "1px" : undefined,
  "&:hover": enabled
    ? {
        color: "#1976d2",
        textDecoration: "underline !important",
      }
    : {},
});

const formatAmount = (value) =>
  Math.round(Number(value || 0)).toLocaleString("en-IN");

const getInvoiceCreatedTime = (invoice = {}) => {
  const dateValue =
    invoice.createdAt || invoice.created_at || invoice.invoiceDate || invoice.date || invoice.updatedAt;
  const parsedDate = dateValue ? new Date(dateValue).getTime() : 0;

  if (!Number.isNaN(parsedDate) && parsedDate > 0) {
    return parsedDate;
  }

  const objectId = invoice._id || invoice.id;
  if (typeof objectId === "string" && /^[a-f\d]{24}$/i.test(objectId)) {
    return parseInt(objectId.substring(0, 8), 16) * 1000;
  }

  return 0;
};

const sortInvoicesByLatestCreated = (invoiceList = []) =>
  [...invoiceList].sort(
    (firstInvoice, secondInvoice) =>
      getInvoiceCreatedTime(secondInvoice) - getInvoiceCreatedTime(firstInvoice)
  );

const isPaidInvoice = (invoice = {}) =>
  String(invoice.status || "").toUpperCase() === "PAID";

const hasClosedClass = (invoice = {}) => {
  const session = Array.isArray(invoice.classSessionId)
    ? invoice.classSessionId[0]
    : invoice.classSessionId;
  const classStatus = String(
    session?.status || invoice.classSessionDoc?.status || invoice.classSessionStatusUpper || ""
  ).trim().toUpperCase();

  return classStatus === "CANCELLED" || classStatus === "COMPLETED";
};

const getPaidInvoices = (invoice = {}) => {
  const groupedInvoices = Array.isArray(invoice.invoices) ? invoice.invoices : [];
  const paidInvoices = groupedInvoices.filter(isPaidInvoice);

  if (paidInvoices.length > 0) {
    return paidInvoices;
  }

  return isPaidInvoice(invoice) ? [invoice] : [];
};

const getActualPaidAmount = (invoice = {}) => {
  const paidAmount = Number(invoice.paidAmount);

  if (Number.isFinite(paidAmount) && paidAmount > 0) {
    return paidAmount;
  }

  return getPaidInvoices(invoice).reduce(
    (sum, paidInvoice) => sum + Number(paidInvoice.totalAmount || 0),
    0
  );
};

const getCycleIndexForInvoice = (paidInvoice = {}) => {
  if (Number.isInteger(paidInvoice.sourceCycleIndex)) {
    return paidInvoice.sourceCycleIndex;
  }

  const cycleNumber = Number(paidInvoice.sourceCycleNumber);
  if (Number.isFinite(cycleNumber) && cycleNumber > 0) {
    return cycleNumber - 1;
  }

  return 0;
};

const getClassCount = (value) => {
  const classCount = Number(value);
  return Number.isFinite(classCount) && classCount > 0 ? classCount : 0;
};

const getCompletedClassesForSession = (classSession = {}) => {
  const cycles = Array.isArray(classSession.cycles) ? classSession.cycles : [];

  if (cycles.length > 0) {
    return cycles.reduce((sum, cycle) => {
      const totalClasses = getClassCount(cycle.totalNoOfClasses);
      const completedClasses = getClassCount(cycle.completedClasses);

      return sum + Math.min(completedClasses, totalClasses || completedClasses);
    }, 0);
  }

  return getClassCount(classSession.completedClasses);
};

const getConsumedAmountForSourceCycleInvoice = (paidInvoice = {}, classSession = {}) => {
  const cycles = Array.isArray(classSession.cycles) ? classSession.cycles : [];
  const cycle = cycles[getCycleIndexForInvoice(paidInvoice)] || {};
  const totalClasses = getClassCount(
    paidInvoice.totalClasses || cycle.totalNoOfClasses
  );
  const completedClasses = getClassCount(cycle.completedClasses);
  const paidAmount = Number(paidInvoice.totalAmount || 0);
  const perClassRate = totalClasses > 0 ? paidAmount / totalClasses : 0;

  return Math.min(completedClasses, totalClasses) * perClassRate;
};

const getConsumedAmountForPaidInvoices = (invoice = {}, classSession = {}) => {
  const paidInvoices = getPaidInvoices(invoice);
  const sortedPaidInvoices = [...paidInvoices].sort(
    (firstInvoice, secondInvoice) =>
      getInvoiceCreatedTime(firstInvoice) - getInvoiceCreatedTime(secondInvoice)
  );
  const hasPackageLevelInvoice = sortedPaidInvoices.some(
    (paidInvoice) =>
      !Number.isInteger(paidInvoice.sourceCycleIndex) &&
      !Number.isFinite(Number(paidInvoice.sourceCycleNumber))
  );

  if (!hasPackageLevelInvoice) {
    return Math.round(
      sortedPaidInvoices.reduce(
        (sum, paidInvoice) =>
          sum + getConsumedAmountForSourceCycleInvoice(paidInvoice, classSession),
        0
      )
    );
  }

  const completedClasses = getCompletedClassesForSession(classSession);
  let allocatedPaidClasses = 0;

  return Math.round(
    sortedPaidInvoices.reduce((sum, paidInvoice) => {
      const totalClasses = getClassCount(paidInvoice.totalClasses);
      const paidAmount = Number(paidInvoice.totalAmount || 0);
      const perClassRate = totalClasses > 0 ? paidAmount / totalClasses : 0;
      const consumedClasses = Math.min(
        totalClasses,
        Math.max(0, completedClasses - allocatedPaidClasses)
      );

      allocatedPaidClasses += totalClasses;

      return sum + consumedClasses * perClassRate;
    }, 0)
  );
};

function Tables() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState({ field: null, direction: "asc" });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const allInvoices = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await getAllRaiseInvoices({ page, limit: 100 });
        if (!response.success) break;

        allInvoices.push(...(response.data || []));
        totalPages = response.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      setInvoices(
        sortInvoicesByLatestCreated(allInvoices).filter(
          (invoice) => !hasClosedClass(invoice) && getActualPaidAmount(invoice) > 0
        )
      );
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceAmounts = (invoice) => {
    const classSession = invoice.classSessionId?.[0] || invoice.classSessionDoc || {};
    const totalAmount = getActualPaidAmount(invoice);
    const usedAmount = Math.min(
      totalAmount,
      getConsumedAmountForPaidInvoices(invoice, classSession)
    );
    return { totalAmount, remainingBalance: Math.max(0, totalAmount - usedAmount) };
  };

  // Sort the full list by the displayed student amount before pagination.
  const sortedInvoices = sort.field
    ? invoices
        .map((invoice) => ({
          invoice,
          amount: getInvoiceAmounts(invoice)[sort.field] / Math.max(1, invoice.students?.length || 0),
        }))
        .sort((a, b) => (a.amount - b.amount) * (sort.direction === "asc" ? 1 : -1))
        .map(({ invoice }) => invoice)
    : invoices;

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedInvoices = sortedInvoices.slice(startIndex, endIndex);
  const totalPages = Math.ceil(invoices.length / rowsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const sortableColumn = (name, field) => ({
    name,
    align: "center",
    headerOpacity: 1,
    label: (
      <TableSortLabel
        active={sort.field === field}
        direction={sort.field === field ? sort.direction : "asc"}
        onClick={() => {
          setSort((previous) => ({
            field,
            direction: previous.field === field && previous.direction === "asc" ? "desc" : "asc",
          }));
          setCurrentPage(1);
        }}
        aria-label={`${name}: sort ${sort.field === field && sort.direction === "asc" ? "descending" : "ascending"}`}
        sx={{
          fontSize: "inherit",
          fontWeight: "inherit",
          color: "#8392ab !important",
          "&:focus-visible": { outline: "3px solid #93c5fd", outlineOffset: "3px" },
          "& .MuiTableSortLabel-icon": {
            opacity: 1,
            color: sort.field !== field
              ? "#8392ab !important"
              : sort.direction === "asc"
              ? "#16a34a !important"
              : "#dc2626 !important",
            fontSize: "28px",
            stroke: "currentColor",
            strokeWidth: 1.5,
            ml: 1,
          },
        }}
      >
        {name.toUpperCase()}
      </TableSortLabel>
    ),
  });

  const columns = [
    { name: "sno", align: "center" },
    { name: "Name", align: "left" },
    sortableColumn("Total paid Amount", "totalAmount"),
    sortableColumn("Balance Amount", "remainingBalance"),
  ];

  // Expand invoices to create separate rows for each student
  const expandedRows = [];
  paginatedInvoices.forEach((invoice, invoiceIndex) => {
    const students = invoice.students || [];

    if (students.length === 0) {
      // If no students, show invoice with N/A
      expandedRows.push({
        invoice,
        student: null,
        studentIndex: 0,
        totalStudents: 1,
        invoiceIndex,
      });
    } else {
      // Create a row for each student
      students.forEach((student, studentIndex) => {
        expandedRows.push({
          invoice,
          student,
          studentIndex,
          totalStudents: students.length,
          invoiceIndex,
        });
      });
    }
  });

  const rows = expandedRows.map((rowData, index) => {
    const { invoice, student, studentIndex, totalStudents } = rowData;
    const parentId = invoice.parentId?._id || invoice.parentId?.id;
    const parentProfilePath = parentId ? `/parents/profile/${parentId}` : undefined;
    const { totalAmount, remainingBalance } = getInvoiceAmounts(invoice);

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {startIndex + rowData.invoiceIndex + 1}
          {totalStudents > 1 && `.${studentIndex + 1}`}
        </ArgonTypography>
      ),
      Name: (
        <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
          <ArgonBox mr={2}>
            <ArgonAvatar
              src={invoice.parentId?.profileImage || ""}
              alt={invoice.parentId?.fullName || "N/A"}
              size="sm"
              variant="full"
            />
          </ArgonBox>
          <ArgonBox display="flex" flexDirection="column">
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              Student: {student?.fullName || student?.name || "N/A"}
            </ArgonTypography>
            <ArgonTypography
              variant="caption"
              color="text"
              fontWeight="medium"
              component={parentProfilePath ? Link : "span"}
              to={parentProfilePath}
              sx={profileLinkSx(Boolean(parentProfilePath))}
            >
              Client: {invoice.parentId?.fullName || "N/A"}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              {invoice.parentId?.email || invoice.tutorId?.email || "N/A"}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              {invoice.parentId?.phone || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      ),
      "Total paid Amount": (
        <ArgonTypography variant="caption" color="success" fontWeight="medium">
          ₹
          {totalStudents > 1
            ? formatAmount(totalAmount / totalStudents)
            : formatAmount(totalAmount)}
          {totalStudents > 1 && (
            <ArgonTypography variant="caption" color="secondary" display="block">
              (Total: ₹{formatAmount(totalAmount)})
            </ArgonTypography>
          )}
        </ArgonTypography>
      ),
      "Balance Amount": (
        <ArgonTypography variant="caption" color="warning" fontWeight="medium">
          ₹
          {totalStudents > 1
            ? formatAmount(remainingBalance / totalStudents)
            : formatAmount(remainingBalance)}
          {totalStudents > 1 && (
            <ArgonTypography variant="caption" color="secondary" display="block">
              (Total: ₹{formatAmount(remainingBalance)})
            </ArgonTypography>
          )}
        </ArgonTypography>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Client Balance
              </ArgonTypography>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Client Balance...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : invoices.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Client Balance found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
            {invoices.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={invoices.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

